/* ---- Event service (API calls for events) ---- */

import type { EventDetail, EventItem, Geofence } from '../types';
import { api } from './api.service';

interface EventListResponse {
  events: EventItem[];
  total: number;
  page: number;
  limit: number;
}

interface GeofenceListResponse {
  geofences: Geofence[];
  total: number;
  page: number;
  limit: number;
}

export interface EventSearchParams {
  fence_name?: string;
  date?: string;
  type?: string;
  page?: number;
  limit?: number;
}

/**
 * GeoJSON FeatureCollection レスポンス (POST /reverse-geocode)。
 * `features[].properties` に住所コンポーネント (PREF_NAME / CITY_NAME / S_NAME) が入る。
 * サーバ実装: src/server/src/services/geocoding.service.ts (`getAddressesByMeshCodes`)
 */
export interface ReverseGeocodeFeatureProperties {
  KEY_CODE: string;
  PREF_NAME: string;
  CITY_NAME: string;
  S_NAME: string;
  center?: { lat: number; lng: number };
  bbox?: number[];
  mesh_codes?: string[];
}

export interface ReverseGeocodeFeature {
  type: 'Feature';
  geometry: object;
  properties: ReverseGeocodeFeatureProperties;
}

export interface ReverseGeocodeResponse {
  type: 'FeatureCollection';
  properties?: {
    center_mesh_code?: string | null;
    adjacent_mesh_codes?: string[];
    mesh_codes?: string[];
    query?: { lat: number; lng: number };
  };
  features: ReverseGeocodeFeature[];
}

class EventService {
  /** Fetch event list with optional filters */
  async listEvents(params?: EventSearchParams): Promise<EventListResponse> {
    const qp: Record<string, string> = {};
    if (params?.fence_name) qp.fence_name = params.fence_name;
    if (params?.date) qp.date = params.date;
    if (params?.type) qp.type = params.type;
    if (params?.page) qp.page = String(params.page);
    if (params?.limit) qp.limit = String(params.limit);
    return api.get<EventListResponse>('/events', qp);
  }

  /** Fetch event detail by ID */
  async getEvent(id: number): Promise<EventDetail> {
    return api.get<EventDetail>(`/events/${id}`);
  }

  /** Fetch geofence list (for map display) */
  async listGeofences(): Promise<GeofenceListResponse> {
    return api.get<GeofenceListResponse>('/geofences', { limit: '100' });
  }

  /**
   * iOS/browser fallback: SDK の onLocationContext が発火しない環境向けに
   * サーバ API で逆ジオコーディングを実行する。Android では SDK 経由で住所を
   * 取得しており本メソッドは呼ばれない (CLAUDE.md 参照)。
   */
  async reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeResponse> {
    return api.post<ReverseGeocodeResponse>('/reverse-geocode', { lat, lng });
  }
}

export const eventService = new EventService();
